import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * P4-X4 — Admin Transfer Card modal
 *
 * Reuses the P4-X2 effective-dated history foundation and the P4-X3A
 * shared invoice-conflict validator. Admin-only. No customer-role
 * pathway exists.
 */
function _todayIso() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

export default function AdminTransferCardModal({ open, onClose, onSubmitted }) {
  const [network, setNetwork] = useState("");
  const [cards, setCards] = useState([]);
  const [cardId, setCardId] = useState("");
  const [transferType, setTransferType] = useState("customer");
  const [destCustomer, setDestCustomer] = useState("");
  const [destUnit, setDestUnit] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(_todayIso());
  const [note, setNote] = useState("");
  const [ctx, setCtx] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [ack, setAck] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const NETWORKS = [
    { value: "esso", label: "Esso CAD/L" },
    { value: "pipeline", label: "Pipeline CAD/L" },
    { value: "petro_pass", label: "Petro-Pass" },
    { value: "ta_petro", label: "Esso USD/GAL" },
  ];

  useEffect(() => {
    if (!open) return;
    setNetwork(""); setCards([]); setCardId(""); setTransferType("customer");
    setDestCustomer(""); setDestUnit(""); setEffectiveDate(_todayIso());
    setNote(""); setCtx(null); setAck(null); setShowConfirm(false);
  }, [open]);

  useEffect(() => {
    if (!network) { setCards([]); setCardId(""); return; }
    (async () => {
      try {
        const { data } = await api.get("/cards", { params: { network } });
        setCards(data || []);
        setCardId("");
        setCtx(null);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load cards.");
      }
    })();
  }, [network]);

  useEffect(() => {
    if (!cardId) { setCtx(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/admin/cards/${cardId}/transfer-options`);
        setCtx(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load transfer options.");
        setCtx(null);
      }
    })();
  }, [cardId]);

  const selectedCard = useMemo(
    () => cards.find(c => c.card_id === cardId) || null, [cards, cardId],
  );

  const isBackdated = effectiveDate && effectiveDate < _todayIso();

  const readyToConfirm = () => {
    if (!network || !cardId || !effectiveDate) return false;
    if (transferType === "customer") return !!destCustomer;
    if (transferType === "unit") return !!destUnit;
    return false;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        transfer_type: transferType,
        effective_date: effectiveDate,
        note: note || "",
      };
      if (transferType === "customer") payload.destination_customer_id = destCustomer;
      else payload.destination_unit = destUnit;
      const { data } = await api.post(`/admin/cards/${cardId}/transfer`, payload);
      setAck(data);
      toast.success(data.message || "Transfer created.");
      if (onSubmitted) onSubmitted(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed.");
      setShowConfirm(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-transfer-modal"
    >
      <div
        className="gf-card w-full max-w-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="gf-card-header">
          <div>
            <h2 className="text-lg font-semibold">Transfer Card</h2>
            <div className="text-xs text-zinc-500">
              Assign a fuel card to another customer or unit using an
              effective date. Invoiced transactions are protected.
            </div>
          </div>
          <button
            className="gf-btn gf-btn-ghost"
            onClick={onClose}
            data-testid="admin-transfer-close"
          >Close</button>
        </div>

        {ack ? (
          <div className="p-4 space-y-2 text-sm">
            <div className="text-green-700 font-medium">
              {ack.message}
            </div>
            <div>Transfer type: <b>{ack.transfer_type}</b></div>
            <div>Previous: <span className="font-mono">{ack.previous_value || "—"}</span></div>
            <div>New: <span className="font-mono">{ack.new_value}</span></div>
            <div>Effective date: <span className="font-mono">{ack.effective_date}</span></div>
            <div>History status: <b>{ack.history_status}</b></div>
            <div className="text-zinc-500">
              History id: <span className="font-mono">{ack.history_id}</span>
            </div>
            {ack.affected_non_invoiced_count > 0 && (
              <div className="text-amber-700 text-xs">
                {ack.affected_non_invoiced_count} non-invoiced transaction(s)
                fall within the effective range. Their stored attribution is
                unchanged (P4-N2 snapshot integrity); the P4-X2 resolver
                applies the new attribution for reports going forward.
              </div>
            )}
            <div className="flex justify-end">
              <button className="gf-btn gf-btn-primary" onClick={onClose} data-testid="admin-transfer-done">
                Done
              </button>
            </div>
          </div>
        ) : showConfirm ? (
          <div className="p-4 space-y-2 text-sm">
            <div className="text-zinc-600 text-xs uppercase tracking-wider">Confirm transfer</div>
            <div>Card: <span className="font-mono">{selectedCard?.card_number}</span></div>
            <div>Network: <b>{network}</b></div>
            <div>Current customer: <span className="font-mono">{ctx?.card?.current_customer_id || "—"}</span></div>
            <div>Current unit: <span className="font-mono">{ctx?.card?.current_unit || "—"}</span></div>
            <div>Transfer type: <b>{transferType}</b></div>
            <div>Destination: <span className="font-mono">
              {transferType === "customer" ? destCustomer : destUnit}
            </span></div>
            <div>Effective date: <span className="font-mono">{effectiveDate}</span></div>
            {isBackdated && (
              <div className="text-amber-700 text-xs" data-testid="admin-transfer-backdated-warn">
                This change is backdated and may affect existing non-invoiced
                transaction history. Invoiced transactions cannot be
                reassigned.
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button className="gf-btn gf-btn-ghost" onClick={() => setShowConfirm(false)} data-testid="admin-transfer-back">
                Back
              </button>
              <button
                className="gf-btn gf-btn-primary"
                onClick={submit}
                disabled={submitting}
                data-testid="admin-transfer-confirm"
              >
                {submitting ? "Submitting…" : "Confirm transfer"}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-3 text-sm">
            <div>
              <label className="overline text-[10px]">NETWORK</label>
              <select
                className="gf-input w-full"
                value={network}
                onChange={(e) => setNetwork(e.target.value)}
                data-testid="admin-transfer-network"
              >
                <option value="">Select network…</option>
                {NETWORKS.map(n => (
                  <option key={n.value} value={n.value}>{n.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="overline text-[10px]">CARD</label>
              <select
                className="gf-input w-full"
                value={cardId}
                onChange={(e) => setCardId(e.target.value)}
                disabled={!network}
                data-testid="admin-transfer-card"
              >
                <option value="">
                  {network ? `Select card (${cards.length} on ${network})…` : "Choose a network first"}
                </option>
                {cards.map(c => (
                  <option key={c.card_id} value={c.card_id}>
                    {c.card_number} · Unit {c.unit || "—"} · {c.customer_name || c.customer_id || "unassigned"} · {c.status}
                  </option>
                ))}
              </select>
            </div>

            {cardId && ctx && (
              <>
                <div>
                  <label className="overline text-[10px]">TRANSFER TO</label>
                  <div className="flex gap-2">
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="radio"
                        name="ttype"
                        value="customer"
                        checked={transferType === "customer"}
                        onChange={() => setTransferType("customer")}
                        data-testid="admin-transfer-type-customer"
                      /> Customer
                    </label>
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="radio"
                        name="ttype"
                        value="unit"
                        checked={transferType === "unit"}
                        onChange={() => setTransferType("unit")}
                        data-testid="admin-transfer-type-unit"
                      /> Unit #
                    </label>
                  </div>
                </div>

                {transferType === "customer" ? (
                  <>
                    <div>
                      <label className="overline text-[10px]">CURRENT CUSTOMER</label>
                      <input className="gf-input w-full" value={ctx.card.current_customer_id || ""} readOnly />
                    </div>
                    <div>
                      <label className="overline text-[10px]">TRANSFER TO CUSTOMER</label>
                      <select
                        className="gf-input w-full"
                        value={destCustomer}
                        onChange={(e) => setDestCustomer(e.target.value)}
                        data-testid="admin-transfer-dest-customer"
                      >
                        <option value="">
                          Select customer ({ctx.eligible_customers.length} eligible)…
                        </option>
                        {ctx.eligible_customers.map(c => (
                          <option key={c.customer_id} value={c.customer_id}>
                            {c.company_name} · {c.customer_id}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="overline text-[10px]">CURRENT UNIT</label>
                      <input className="gf-input w-full" value={ctx.card.current_unit || ""} readOnly />
                    </div>
                    <div>
                      <label className="overline text-[10px]">NEW UNIT</label>
                      <select
                        className="gf-input w-full"
                        value={destUnit}
                        onChange={(e) => setDestUnit(e.target.value)}
                        data-testid="admin-transfer-dest-unit"
                      >
                        <option value="">
                          Select unit ({ctx.eligible_units.length} in customer's fleet)…
                        </option>
                        {ctx.eligible_units.map(u => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                <div>
                  <label className="overline text-[10px]">EFFECTIVE DATE</label>
                  <input
                    type="date"
                    className="gf-input w-full"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                    data-testid="admin-transfer-effective-date"
                  />
                  <div className="text-xs text-zinc-500 mt-1">
                    Past, today, or future. Backdated transfers with
                    already-invoiced affected transactions are blocked.
                  </div>
                </div>

                <div>
                  <label className="overline text-[10px]">NOTE (OPTIONAL)</label>
                  <input
                    className="gf-input w-full"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    data-testid="admin-transfer-note"
                    placeholder="e.g., Fleet reassignment #12345"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <button className="gf-btn gf-btn-ghost" onClick={onClose} data-testid="admin-transfer-cancel">
                    Cancel
                  </button>
                  <button
                    className="gf-btn gf-btn-primary"
                    disabled={!readyToConfirm()}
                    onClick={() => setShowConfirm(true)}
                    data-testid="admin-transfer-review"
                  >
                    Review
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
